import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import type { AgentChatDecision } from '@/services/agents/chat-stream';
import {
  promptQueueKey,
  useChatPromptQueueStore,
} from '@/stores/chat-prompt-queue-store';

import type { ConversationWriteAccess } from './conversation-access';
import type { ChatAttachmentRef } from './use-agent-chat';

/** What the composer and the queue strip need from the queue. */
export interface PromptQueueControls {
  /** This conversation's bucket in the queue store. */
  queueKey: string;
  enqueue: (text: string) => void;
  /**
   * Lift a hold and, when no turn is streaming, dispatch the next prompt
   * immediately. Also how a reloaded queue is set going again.
   */
  resume: () => void;
  /**
   * Stop the live turn AND hold the queue: the user interrupted the agent
   * to intervene, so firing the next prompt right into that would undo the
   * interruption.
   */
  stopTurn: () => void;
}

interface UsePromptQueueDispatchOptions {
  orgId?: string;
  agentId?: string;
  conversationId: string | null;
  /**
   * The id this chat's own stream minted mid-turn, if any -- the one key
   * change that means "the draft became this conversation" rather than
   * "the user went somewhere else" (see the draft handoff below).
   */
  adoptedConversationId: string | null;
  isSending: boolean;
  error: string | null;
  /** Set while the turn ended on a question the composer is showing. */
  pendingDecision: AgentChatDecision | null;
  writeAccess: ConversationWriteAccess;
  /** Resolves false when the send was refused outright (nothing went out). */
  sendMessage: (
    text: string,
    attachments?: ChatAttachmentRef[],
  ) => Promise<boolean | void>;
  stopTurn: () => void;
}

/**
 * FIFO auto-dispatch for prompts queued while a turn streams.
 *
 * Keys off the same signal that re-enables the composer today -- the
 * isSending true->false transition, which useAgentChat only makes once the
 * stream has fully settled (done, error, or abort) -- rather than any
 * particular SSE frame, so "turn truly complete" here can never disagree
 * with what the composer shows.
 *
 * The queue HOLDS instead of dispatching when the turn ended badly or
 * interactively:
 *   - the turn errored (don't auto-fire into a broken backend),
 *   - the user stopped it by hand (see PromptQueueControls.stopTurn),
 *   - the turn ended on a decision the composer is showing (a queued
 *     follow-up should not steamroll a question the agent is waiting on).
 * A hold is lifted by the strip's resume button, or by any manual send --
 * a fresh turn starting is the user telling us the path works again.
 */
export function usePromptQueueDispatch({
  orgId,
  agentId,
  conversationId,
  adoptedConversationId,
  isSending,
  error,
  pendingDecision,
  writeAccess,
  sendMessage,
  stopTurn,
}: UsePromptQueueDispatchOptions): PromptQueueControls {
  const queueKey = promptQueueKey(orgId, agentId, conversationId);

  const queueKeyRef = useRef(queueKey);
  const errorRef = useRef(error);
  errorRef.current = error;
  const pendingDecisionRef = useRef(pendingDecision);
  pendingDecisionRef.current = pendingDecision;
  const writeAccessRef = useRef(writeAccess);
  writeAccessRef.current = writeAccess;
  // Seeded false, NOT with the prop: a hook that mounts while a turn is
  // already streaming must still see that turn "start" on its first effect
  // run, or the completion transition would go unrecorded and undispatched.
  const isSendingRef = useRef(false);
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  // The bucket the streaming turn was sent against. Dispatch compares it to
  // the current key so that leaving for another conversation mid-turn (which
  // also flips isSending off, via releaseActiveStream) never fires the NEW
  // conversation's queue off the OLD conversation's ending.
  const sendingKeyRef = useRef<string | null>(null);

  const adoptedIdRef = useRef(adoptedConversationId);
  adoptedIdRef.current = adoptedConversationId;
  // The adoption the previous key change already accounted for.
  // adoptedConversationId is never cleared, so without this a later draft
  // navigating back into that chat would read as a fresh mint.
  const consumedAdoptionRef = useRef<string | null>(null);

  // A brand-new chat queues under a draft key until the stream's start frame
  // mints the conversation id mid-turn; carry the queue (and the live turn's
  // bucket) over so those prompts still dispatch and persist under the id.
  //
  // Gated on the id having been ADOPTED by this chat's own stream: merely
  // navigating from an abandoned draft into some other conversation changes
  // the key the same way, and moving the draft's prompts there would let
  // them auto-send into a chat they were never written for.
  //
  // A layout effect rather than render (a store write during render can be
  // replayed or discarded by concurrent React) or a passive effect (the ref
  // must be current before any event handler can enqueue against it).
  useLayoutEffect(() => {
    const prev = queueKeyRef.current;
    if (prev === queueKey) return;
    queueKeyRef.current = queueKey;
    const adopted = adoptedIdRef.current;
    const freshAdoption =
      adopted !== consumedAdoptionRef.current ? adopted : null;
    consumedAdoptionRef.current = adopted;
    const draftKey = promptQueueKey(orgId, agentId, null);
    if (
      prev === draftKey &&
      conversationId !== null &&
      conversationId === freshAdoption
    ) {
      useChatPromptQueueStore.getState().reassign(prev, queueKey);
      if (sendingKeyRef.current === prev) sendingKeyRef.current = queueKey;
    }
  }, [queueKey, orgId, agentId, conversationId]);

  const dispatchNext = useCallback((key: string) => {
    // sendMessage refuses non-writable conversations silently; checking
    // first keeps the prompt queued instead of losing it to the no-op.
    if (writeAccessRef.current !== 'allowed') return;
    const next = useChatPromptQueueStore.getState().dequeue(key);
    if (!next) return;
    void sendMessageRef.current(next.text).then((acted) => {
      // Refused outright (most likely another turn already had the
      // stream): nothing went out, so the prompt goes back to the head
      // for the next completion to pick up.
      if (acted === false) {
        useChatPromptQueueStore.getState().requeueFront(key, next);
      }
    });
  }, []);

  useEffect(() => {
    const wasSending = isSendingRef.current;
    isSendingRef.current = isSending;
    const store = useChatPromptQueueStore.getState();
    if (isSending && !wasSending) {
      sendingKeyRef.current = queueKey;
      // Any manually started turn lifts a hold: the user has shown the
      // path works (or answered the question) themselves.
      store.setHeld(queueKey, false);
      return;
    }
    if (!isSending && wasSending) {
      const endedKey = sendingKeyRef.current;
      sendingKeyRef.current = null;
      if (endedKey !== queueKey) return;
      if (errorRef.current || pendingDecisionRef.current) {
        store.setHeld(queueKey, true);
        return;
      }
      if (store.heldKeys[queueKey]) return;
      dispatchNext(queueKey);
    }
  }, [isSending, queueKey, dispatchNext]);

  const enqueue = useCallback(
    (text: string) => {
      useChatPromptQueueStore.getState().enqueue(queueKeyRef.current, text);
    },
    // queueKeyRef tracks the key; the callback can stay stable.
    [],
  );

  const resume = useCallback(() => {
    useChatPromptQueueStore.getState().setHeld(queueKeyRef.current, false);
    if (!isSendingRef.current) dispatchNext(queueKeyRef.current);
  }, [dispatchNext]);

  const stopTurnRef = useRef(stopTurn);
  stopTurnRef.current = stopTurn;
  const stopTurnHoldingQueue = useCallback(() => {
    // Held before the stop lands: the abort flips isSending synchronously,
    // and the dispatch effect must already see the hold when it runs.
    useChatPromptQueueStore.getState().setHeld(queueKeyRef.current, true);
    stopTurnRef.current();
  }, []);

  return useMemo(
    () => ({
      queueKey,
      enqueue,
      resume,
      stopTurn: stopTurnHoldingQueue,
    }),
    [queueKey, enqueue, resume, stopTurnHoldingQueue],
  );
}
